<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PriceRuler from 'dashboard/components-next/qpreco/PriceRuler.vue';
import { formatCurrency } from '../utils/qprecoFormatters';

defineProps({
  priceSectionTitle: { type: String, default: '' },
  confidenceTitle: { type: String, default: '' },
  confidenceBody: { type: String, default: '' },
  priceHeading: { type: String, default: '' },
  currentPrice: { type: Number, default: 0 },
  suggestedPrice: { type: Number, default: 0 },
  maxSuggestion: { type: Number, default: 0 },
  pricePerArea: { type: Number, default: 0 },
  canEditPrice: { type: [Boolean, Number, String], default: false },
  editingPrice: { type: Boolean, default: false },
  savingPrice: { type: Boolean, default: false },
  priceQuestion: { type: String, default: '' },
  priceError: { type: String, default: '' },
  priceRangeLabel: { type: String, default: '' },
  rulerMarkerValue: { type: Number, default: 0 },
  rulerMinPrice: { type: Number, default: 0 },
  rulerMaxPrice: { type: Number, default: 0 },
  rulerPercentiles: { type: Object, default: () => ({}) },
  rulerMarkerColor: { type: String, default: 'gray' },
  rulerVariant: { type: String, default: '' },
  showPositivePriceAlert: { type: [Boolean, Number], default: false },
  showHighPriceAlert: { type: [Boolean, Number], default: false },
});

defineEmits(['start-edit-price', 'cancel-edit-price', 'save-price']);

const priceInput = defineModel('priceInput', {
  type: [String, Number],
  default: '',
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard>
    <div class="grid gap-5">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h3
              class="text-[16px] font-semibold text-lais-text font-redHatDisplay"
            >
              {{ priceSectionTitle }}
            </h3>
            <span
              class="rounded-full bg-lais-purple-50 px-2 py-1 text-[11px] font-semibold text-lais-primary"
            >
              {{ confidenceTitle }}
            </span>
          </div>
          <p class="mt-1 text-[13px] text-lais-text-muted">
            {{ priceHeading }}
          </p>
          <p
            class="mt-2 text-[32px] leading-none font-semibold text-lais-text font-redHatDisplay"
          >
            {{ formatCurrency(currentPrice || suggestedPrice) }}
          </p>
          <p v-if="pricePerArea" class="mt-2 text-[13px] text-lais-text-muted">
            {{ formatCurrency(pricePerArea) }}/m²
          </p>
        </div>

        <LaisPrimaryButton
          v-if="!editingPrice && canEditPrice"
          variant="ghost"
          icon="i-lucide-pencil"
          @click="$emit('start-edit-price')"
        >
          Alterar valor
        </LaisPrimaryButton>
      </div>

      <PriceRuler
        :value="rulerMarkerValue"
        :min="rulerMinPrice"
        :max="rulerMaxPrice"
        :percentiles="rulerPercentiles"
        :marker-color="rulerMarkerColor"
        :variant="rulerVariant"
        :format-value="formatCurrency"
      />

      <div
        class="rounded-lais border border-lais-border-soft bg-lais-bg-muted px-4 py-3"
      >
        <p class="text-[13px] font-semibold text-lais-text">
          {{ confidenceTitle }}
        </p>
        <p class="mt-1 text-[13px] text-lais-text-muted">
          {{ confidenceBody }}
        </p>
      </div>

      <div
        v-if="editingPrice"
        class="rounded-lais border border-lais-border-soft px-4 py-4 grid gap-3"
      >
        <label
          class="text-[13px] font-semibold text-lais-text"
          for="qpreco-price-input"
        >
          {{ priceQuestion }}
        </label>
        <input
          id="qpreco-price-input"
          v-model="priceInput"
          type="text"
          inputmode="numeric"
          pattern="[0-9.]*"
          class="w-full rounded-lais border border-lais-border px-3 py-2 text-[14px] text-lais-text"
        />
        <p v-if="priceError" class="text-[12px] text-rose-700">
          {{ priceError }}
        </p>
        <p v-else class="text-[12px] text-lais-text-muted">
          O valor deve estar entre {{ priceRangeLabel }}.
        </p>
        <div class="flex flex-wrap justify-end gap-2">
          <LaisPrimaryButton
            variant="ghost"
            @click="$emit('cancel-edit-price')"
          >
            Cancelar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            :disabled="savingPrice"
            @click="$emit('save-price')"
          >
            {{ savingPrice ? 'Salvando...' : 'Alterar valor' }}
          </LaisPrimaryButton>
        </div>
      </div>

      <div
        v-if="showPositivePriceAlert || showHighPriceAlert"
        class="rounded-lais border px-4 py-3"
        :class="[
          showHighPriceAlert
            ? 'border-amber-200 bg-[#fff8e1] text-amber-900'
            : 'border-emerald-200 bg-emerald-50 text-emerald-900',
        ]"
      >
        <p class="text-[13px] font-semibold">
          {{
            showHighPriceAlert
              ? 'O valor está acima do valor máximo sugerido'
              : 'Excelente'
          }}
        </p>
        <p class="mt-1 text-[13px]">
          {{
            showHighPriceAlert
              ? `Imóveis como este costumam atrair menos interessados quando têm preço acima de ${formatCurrency(maxSuggestion)}`
              : 'Preços dentro da faixa recomendada geram mais visitas'
          }}
        </p>
      </div>
    </div>
  </LaisCard>
</template>
